import { Outlet } from "react-router";

import { Stack } from "@mui/material";
import { useAssetInventoryHasWarning, useBankruptcyChecklistHasWarning, useHasInactiveClaim } from "@shared";

import { useUserSettings } from "@/common/hooks/useUserSettings";
import { ClientTab } from "@/router/ClientRoutes";

import { clientFileGroundSx, clientFileSheetSx } from "./Client.styles";
import { getClientFileTint } from "./clientFileTints";
import { ClientChangeInfoProvider } from "./components/change-history/ClientChangeInfoProvider";
import { ClientHeader } from "./components/client-header/ClientHeader";
import { DebtSettlementHeader } from "./components/tabs/debt-settlement-tab/debt-settlement-header/DebtSettlementHeader";
import { useClient } from "./hooks/useClient";
import { useActiveClientTab } from "./hooks/useClientTabs";

export function Client() {
  const { client } = useClient();
  const activeTab = useActiveClientTab();
  const hasInactiveClaim = useHasInactiveClaim(client?.clientId);
  const hasChecklistWarning = useBankruptcyChecklistHasWarning(client?.clientId);
  const hasAssetInventoryWarning = useAssetInventoryHasWarning(client?.clientId);
  // Read without waiting: the setting is warmed at session bootstrap, and a slow or failing request
  // for it must cost the tint, never the page.
  const { isClientFileColorCodingOn } = useUserSettings();

  if (!client) {
    return null;
  }

  const subTabs = activeTab === ClientTab.DebtSettlement ? <DebtSettlementHeader /> : null;
  const tint = isClientFileColorCodingOn ? getClientFileTint(client.tintIndex) : undefined;

  return (
    // The ground bleeds into the layout's <main> padding, which assumes <main> keeps py: 0 and this
    // stack stays its direct flex child.
    <Stack flex={1} sx={clientFileGroundSx(tint)}>
      <Stack spacing={3} flex={1} overflow="hidden" sx={clientFileSheetSx(!!tint)}>
        <ClientHeader
          client={client}
          subTabs={subTabs}
          tabWarnings={{
            [ClientTab.DebtSettlement]: hasInactiveClaim || hasChecklistWarning || hasAssetInventoryWarning,
          }}
        />
        <ClientChangeInfoProvider>
          <Outlet context={client} />
        </ClientChangeInfoProvider>
      </Stack>
    </Stack>
  );
}
